import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { firstValueFrom } from 'rxjs';
import { RateManagerService } from 'src/app/services/modules/instant-rate-manager/rate-manager.service';
import { AlertUtil } from 'src/app/shared/utils/alert-dialog.util';

@Component({
  selector: 'app-bulk-create-rate-modal',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, NgSelectModule],
  templateUrl: './bulk-create-rate-modal.component.html',
  styleUrl: './bulk-create-rate-modal.component.scss',
})
export class BulkCreateRateModalComponent implements OnInit {
  rateForm!: FormGroup;
  isSubmitting = false;

  locationTypeArray = [
    { value: 'door', label: 'Door' },
    { value: 'depot', label: 'Depot' },
  ];

  categoryTypeArray: Array<any> = [];
  selectedCategoryID!: number;
  selectedCategory: string = '';

  currentStep: number = 1;

  isSaveButtonEnabled: boolean = false;

  isDragOver = false;
  uploadedFile: File | null = null;

  isPreviewed = false;
  previewData: any = null;
  activeTab: 'errors' | 'warnings' = 'errors';

  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  private activeModal = inject(NgbActiveModal);
  modalService = inject(NgbModal);
  rateManagerService = inject(RateManagerService);
  fb = inject(FormBuilder);

  ngOnInit() {
    this.rateForm = this.fb.group({
      rate_category_id: ['', Validators.required],
      rate_category_name: ['', Validators.required],
      file: [null, Validators.required],
    });

    this.getCategory();
  }

  closeModal = () => {
    this.modalService.dismissAll();
  };

  async getCategory() {
    try {
      const response =
        await this.rateManagerService.getProviderRatesCategories();
      const mapped = response.data.map((item: any) => ({
        value: item.id,
        label: item.name
          ? item.name.charAt(0).toUpperCase() + item.name.slice(1)
          : item.name,
        description: item.description,
      }));

      this.categoryTypeArray = mapped;

      return mapped;
    } catch (error) {
      console.error('Error fetching category types:', error);
      this.categoryTypeArray = [];
    }
  }

  onChangeCategory = async (category: any) => {
    this.selectedCategoryID = category.value;
    this.selectedCategory = category.label;

    this.rateForm.patchValue({
      rate_category_id: category.value,
      rate_category_name: category.label,
    });
  };

  goToPrevStep = () => {
    if (this.isPreviewed) {
      this.isPreviewed = false;
      this.previewData = null;
      this.activeTab = 'errors';
      this.uploadedFile = null;
      this.rateForm.patchValue({ file: null });
      this.resetFileInput();
      return;
    }

    if (this.currentStep > 1) {
      this.currentStep--;

      if (this.currentStep === 1) {
        this.uploadedFile = null;
        this.rateForm.patchValue({ file: null });
        this.resetFileInput();
      }
    }

    // Disable save button on step 1
    this.isSaveButtonEnabled = this.currentStep > 1;
  };

  private resetFileInput() {
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  goToNextStep = async () => {
    this.currentStep++;

    switch (this.currentStep) {
      case 2:
        if (!this.selectedCategoryID) return;

        this.isSaveButtonEnabled = true;
        break;

      default:
        break;
    }
  };

  handleBulkUpload() {
    if (!this.uploadedFile) {
      alert('Please select a CSV file before submitting.');
      return;
    }

    this.rateForm.patchValue({ file: this.uploadedFile });

    this.submitBulkRate();
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;
  }

  onFileSelect(event: any) {
    const file = event.target.files[0];
    this.handleFile(file);
  }

  onFileDrop(event: DragEvent) {
    event.preventDefault();
    this.isDragOver = false;

    if (event.dataTransfer?.files.length) {
      const file = event.dataTransfer.files[0];
      this.handleFile(file);
    }
  }

  handleFile(file: File) {
    console.log('hanlde file', file);

    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      alert('Only CSV files are allowed.');
      return;
    }

    this.uploadedFile = file; // <-- the dynamic text will now show this file name
    console.log('Selected file:', file);
  }

  async submitBulkRate() {
    if (this.rateForm.invalid) {
      this.rateForm.markAllAsTouched();
      await AlertUtil.error('Please fill all required fields.');
      return;
    }

    if (!this.uploadedFile) {
      await AlertUtil.error('Please select a CSV file before submitting.');
      return;
    }

    this.isSubmitting = true;

    const buildFormData = (preview: boolean): FormData => {
      const formData = new FormData();
      formData.append('rate_category_id', this.rateForm.get('rate_category_id')?.value);
      formData.append('rate_category_name', this.rateForm.get('rate_category_name')?.value);
      formData.append('file', this.uploadedFile!);
      formData.append('preview', String(preview));
      formData.append('status', 'in_review');
      return formData;
    };

    try {
      if (!this.isPreviewed) {
        // Step 1: Preview — validate without saving. HTTP errors are caught separately
        // so that dialog handling below never falls into the HTTP error handler.
        let response: any;
        try {
          response = await firstValueFrom(this.rateManagerService.bulkCreateRate(buildFormData(true)));
        } catch (err: any) {
          await this.handleUploadError(err);
          return;
        }

        this.previewData = response.data;
        this.isPreviewed = true;

        const hasErrors = (this.previewData?.errors?.length || 0) > 0;
        const hasWarnings = (this.previewData?.warnings?.length || 0) > 0;

        this.activeTab = hasErrors ? 'errors' : (hasWarnings ? 'warnings' : 'errors');

        const html = this.generateUploadUI(this.previewData, 'Please review the summary below before proceeding.');
        if (hasErrors) {
          await AlertUtil.warningConfirm(html, 'Done Verification', 'Review Summary');
        } else {
          await AlertUtil.successConfirm(html, 'Done Verification', 'Review Summary');
        }
      } else {
        // Step 2: Actual submit
        let response: any;
        try {
          response = await firstValueFrom(this.rateManagerService.bulkCreateRate(buildFormData(false)));
        } catch (err: any) {
          await this.handleUploadError(err);
          return;
        }

        const stats = response.data;
        const html = this.generateUploadUI(stats, response.message || 'Bulk rates uploaded successfully.');

        await AlertUtil.successConfirm(html, 'Success');
        this.resetUploadState();
        this.activeModal.close('success');
      }
    } finally {
      this.isSubmitting = false;
    }
  }

  private async handleUploadError(err: any) {
    console.error('Bulk upload error:', err);
    this.isPreviewed = false;
    this.previewData = null;

    const errorBody = err?.error;
    if (errorBody?.error && typeof errorBody.error === 'object') {
      const html = this.generateUploadUI(errorBody.error, errorBody.message || 'Validation failed.');
      await AlertUtil.errorConfirm(html, 'Validation Error');
    } else {
      const specificError = typeof errorBody?.error === 'string' ? errorBody.error : null;
      const msg = specificError || errorBody?.message || err?.message || 'Failed to process file.';
      await AlertUtil.error(msg);
    }
  }

  private resetUploadState() {
    this.rateForm.reset();
    this.uploadedFile = null;
    this.isPreviewed = false;
    this.previewData = null;
    this.activeTab = 'errors';
  }

  setActiveTab(tab: 'errors' | 'warnings') {
    this.activeTab = tab;
  }

  private generateUploadUI(stats: any, message: string): string {
    let errors = stats.errors || [];

    // Fallback for single top-level error message within the stats object
    if (errors.length === 0 && (stats.error || stats.message) && typeof (stats.error || stats.message) === 'string') {
      errors = [{ row: 'File', error: stats.error || stats.message }];
    }

    const warnings = stats.warnings || [];
    const hasErrors = errors.length > 0;
    const hasWarnings = warnings.length > 0;
    const hasTabs = hasErrors || hasWarnings;
    const defaultTab = hasErrors ? 'errors' : 'warnings';

    let html = `
      <div class="text-start" style="font-size: 0.9rem; color: #333;">
        <p class="fw-bold mb-3 ${hasErrors ? 'text-danger' : 'text-success'}" style="font-size: 1.1rem;">
          ${message}
        </p>

        <div class="row g-2 mb-3">
          <div class="col-6">
            <div class="p-2 border rounded bg-light shadow-sm h-100">
              <div class="text-muted small text-uppercase fw-bold" style="font-size: 0.7rem;">Total Rows</div>
              <div class="fs-5 fw-bold">${stats.total_rows || 0}</div>
            </div>
          </div>
          <div class="col-6">
            <div class="p-2 border rounded bg-light shadow-sm h-100">
              <div class="text-muted small text-uppercase fw-bold" style="font-size: 0.7rem;">Success</div>
              <div class="fs-5 fw-bold text-success">${(stats.created_rates || 0) + (stats.updated_rates || 0)}</div>
            </div>
          </div>
          <div class="col-4">
            <div class="p-2 border rounded bg-light shadow-sm h-100">
              <div class="text-muted small text-uppercase fw-bold" style="font-size: 0.7rem;">New</div>
              <div class="fw-bold">${stats.new_routes || 0}</div>
            </div>
          </div>
          <div class="col-4">
            <div class="p-2 border rounded bg-light shadow-sm h-100">
              <div class="text-muted small text-uppercase fw-bold" style="font-size: 0.7rem;">Updated</div>
              <div class="fw-bold">${stats.updated_routes || 0}</div>
            </div>
          </div>
          <div class="col-4">
            <div class="p-2 border rounded bg-light shadow-sm h-100">
              <div class="text-muted small text-uppercase fw-bold" style="font-size: 0.7rem;">Skipped</div>
              <div class="fw-bold ${stats.skipped_routes > 0 ? 'text-warning' : ''}">${stats.skipped_routes || 0}</div>
            </div>
          </div>
        </div>
    `;

    if (hasTabs) {
      const errBtnActiveStyle  = `border-bottom:2px solid #dc3545;color:#dc3545;font-weight:bold;`;
      const warnBtnActiveStyle = `border-bottom:2px solid #e6a817;color:#e6a817;font-weight:bold;`;
      const inactiveBtnStyle   = `border-bottom:2px solid transparent;color:#6c757d;font-weight:normal;`;
      const baseBtnStyle       = `border:none;background:none;padding:8px 16px;cursor:pointer;font-size:0.85rem;margin-bottom:-2px;`;

      const errBtnOnClick = `
        document.getElementById('ul-panel-errors').style.display='block';
        document.getElementById('ul-panel-warnings').style.display='none';
        document.getElementById('ul-btn-errors').style.cssText='${baseBtnStyle}${errBtnActiveStyle}';
        document.getElementById('ul-btn-warnings').style.cssText='${baseBtnStyle}${inactiveBtnStyle}';
      `.replace(/\n\s*/g, '');

      const warnBtnOnClick = `
        document.getElementById('ul-panel-warnings').style.display='block';
        document.getElementById('ul-panel-errors').style.display='none';
        document.getElementById('ul-btn-warnings').style.cssText='${baseBtnStyle}${warnBtnActiveStyle}';
        document.getElementById('ul-btn-errors').style.cssText='${baseBtnStyle}${inactiveBtnStyle}';
      `.replace(/\n\s*/g, '');

      html += `<div class="mt-4">`;

      // Tab nav
      html += `<div style="display:flex;border-bottom:2px solid #dee2e6;">`;
      const isErrorsTab = defaultTab === 'errors';
      const isWarningsTab = defaultTab === 'warnings';

      html += `<button id="ul-btn-errors" onclick="${errBtnOnClick}"
        style="${baseBtnStyle}${isErrorsTab ? errBtnActiveStyle : inactiveBtnStyle}">
        Errors (${errors.length})
      </button>`;

      html += `<button id="ul-btn-warnings" onclick="${warnBtnOnClick}"
        style="${baseBtnStyle}${isWarningsTab ? warnBtnActiveStyle : inactiveBtnStyle}">
        Warnings (${warnings.length})
      </button>`;
      html += `</div>`;

      // Errors panel
      html += `<div id="ul-panel-errors" style="display:${defaultTab === 'errors' ? 'block' : 'none'}">`;
      if (hasErrors) {
        html += `
          <div class="border border-top-0 rounded-bottom" style="max-height:250px;overflow-y:auto;background:#fffcfc;">
            <table class="table table-sm table-hover mb-0" style="font-size:0.85rem;">
              <thead class="bg-white sticky-top shadow-sm">
                <tr>
                  <th class="px-3 py-2 text-center" style="width:70px;border-top:0;">Row</th>
                  <th class="px-3 py-2" style="border-top:0;">Problem Description</th>
                </tr>
              </thead>
              <tbody>
        `;
        errors.forEach((err: any) => {
          html += `
            <tr>
              <td class="px-3 py-2 fw-bold text-center text-secondary bg-light" style="vertical-align:middle;">${err.row}</td>
              <td class="px-3 py-2 text-danger" style="vertical-align:middle;line-height:1.4;">${err.error || err.message}</td>
            </tr>
          `;
        });
        html += `</tbody></table></div>`;
      }
      html += `</div>`;

      // Warnings panel
      html += `<div id="ul-panel-warnings" style="display:${defaultTab === 'warnings' ? 'block' : 'none'}">`;
      if (hasWarnings) {
        html += `
          <div class="border border-top-0 rounded-bottom" style="max-height:250px;overflow-y:auto;background:#fffdf5;">
            <table class="table table-sm table-hover mb-0" style="font-size:0.85rem;">
              <thead class="bg-white sticky-top shadow-sm">
                <tr>
                  <th class="px-3 py-2 text-center" style="width:70px;border-top:0;">Row</th>
                  <th class="px-3 py-2" style="border-top:0;">Warning Description</th>
                </tr>
              </thead>
              <tbody>
        `;
        warnings.forEach((warn: any) => {
          html += `
            <tr>
              <td class="px-3 py-2 fw-bold text-center text-secondary bg-light" style="vertical-align:middle;">${warn.row}</td>
              <td class="px-3 py-2 text-warning" style="vertical-align:middle;line-height:1.4;">${warn.warning || warn.message || warn.error}</td>
            </tr>
          `;
        });
        html += `</tbody></table></div>`;
      }
      html += `</div>`;

      html += `</div>`;
    }

    html += `</div>`;
    return html;
  }

  downloadTemplate = () => {
    const selectedCategoryId = this.selectedCategoryID;
    const selectedCategory = this.selectedCategory;

    this.rateManagerService.downloadRateTemplate(selectedCategoryId).subscribe({
      next: (csvText: string) => {
        const blob = new Blob([csvText], { type: 'text/csv' });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        const timestamp = new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/[-:T]/g, '');
        link.download = `template_${selectedCategory}.csv`;
        link.click();
        window.URL.revokeObjectURL(link.href);
      },
      error: (err: any) => {
        console.error('Failed to download CSV:', err);
      },
    });
  };
}
